import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import FormInput from '../../components/ui/FormInput';

const EMPTY = {
  nome: '', cpf: '', telefone: '', email: '',
  valorImovel: '', valorFinanciado: '',
  prazo: '', dataNascimento: '', observacoes: '',
};

function maskCPF(value) {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

function maskTelefone(value) {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
}

function maskMoeda(value) {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  const num = parseInt(digits, 10);
  return (num / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function ClientModal({ open, onClose, cliente, onSave, onDelete }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const isEdit = !!cliente;

  useEffect(() => {
    if (cliente) {
      setForm({
        nome: cliente.nome || '',
        cpf: cliente.cpf || '',
        telefone: cliente.telefone || '',
        email: cliente.email || '',
        valorImovel: cliente.valorImovel || '',
        valorFinanciado: cliente.valorFinanciado || '',
        prazo: cliente.prazo || '',
        dataNascimento: cliente.dataNascimento || '',
        observacoes: cliente.observacoes || '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [cliente, open]);

  if (!open) return null;

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleCPFChange = (e) => {
    setForm((prev) => ({ ...prev, cpf: maskCPF(e.target.value) }));
  };

  const handleTelefoneChange = (e) => {
    setForm((prev) => ({ ...prev, telefone: maskTelefone(e.target.value) }));
  };

  const handleMoedaChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: maskMoeda(e.target.value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      console.error('Erro ao salvar cliente:', err);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Excluir ${cliente.nome}?`)) return;
    setLoading(true);
    try {
      await onDelete(cliente.id);
      onClose();
    } catch (err) {
      console.error('Erro ao excluir cliente:', err);
    }
    setLoading(false);
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(11,29,53,0.5)', backdropFilter: 'blur(4px)',
        zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div style={{
        background: 'var(--white)', borderRadius: 20, width: '100%', maxWidth: 560,
        maxHeight: '90vh', overflowY: 'auto', padding: 32,
        boxShadow: '0 40px 80px rgba(0,0,0,0.15)',
        animation: 'modalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
      }}>
        <style>{`@keyframes modalIn { from { opacity:0; transform:scale(0.95) translateY(10px); } to { opacity:1; transform:scale(1) translateY(0); } }`}</style>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>
              {isEdit ? 'Editar Cliente' : 'Novo Cliente'}
            </span>
            {isEdit && (
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
                Editando: {cliente.nome}
              </p>
            )}
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--white)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: '0.9rem',
          }}>✕</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormInput label="Nome completo" placeholder="Nome do cliente" required value={form.nome} onChange={handleChange('nome')} />
            <FormInput label="CPF" placeholder="000.000.000-00" required value={form.cpf} onChange={handleCPFChange} maxLength={14} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormInput label="Telefone" placeholder="(61) 99999-9999" required value={form.telefone} onChange={handleTelefoneChange} maxLength={15} />
            <FormInput label="E-mail" type="email" placeholder="cliente@email.com" required value={form.email} onChange={handleChange('email')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormInput label="Valor do Imóvel" placeholder="R$ 500.000,00" required value={form.valorImovel} onChange={handleMoedaChange('valorImovel')} />
            <FormInput label="Valor Financiado" placeholder="R$ 400.000,00" required value={form.valorFinanciado} onChange={handleMoedaChange('valorFinanciado')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormInput label="Prazo (meses)" type="number" placeholder="420" required value={form.prazo} onChange={handleChange('prazo')} />
            <FormInput label="Data de Nascimento" type="date" required value={form.dataNascimento} onChange={handleChange('dataNascimento')} />
          </div>
          <FormInput label="Observações" placeholder="Notas internas sobre o cliente..." value={form.observacoes} onChange={handleChange('observacoes')} />

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', marginTop: 24 }}>
            <div>
              {isEdit && onDelete && (
                <Button variant="danger" type="button" disabled={loading} onClick={handleDelete}>
                  🗑️ Excluir
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
              <Button variant="accent" type="submit" disabled={loading}>
                {loading ? 'Salvando...' : isEdit ? 'Salvar Alterações' : 'Criar Cliente'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}